import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import axios from 'axios';
import { SITE } from '@/constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = [
  'M&A — Sell-side',
  'M&A — Buy-side',
  'Capital Raise',
  'Strategic Advisory',
  'Cross-border · MENA',
  'Cross-border · Hong Kong / APAC',
  'Other',
];

const REGIONS = ['India', 'MENA', 'Hong Kong', 'Other'];

/**
 * "Start a Conversation" dialog — restrained, editorial styling on ink.
 */
export const BookConsultation = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    region: '',
    interest: '',
    message: '',
  });
  const [state, setState] = useState({
    loading: false,
    success: false,
    error: null,
  });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setState({
        loading: false,
        success: false,
        error: 'Name and email are required.',
      });
      return;
    }
    setState({ loading: true, success: false, error: null });
    try {
      await axios.post(`${API}/consultations`, form);
      setState({ loading: false, success: true, error: null });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        'Unable to submit. Please try again.';
      setState({ loading: false, success: false, error: String(detail) });
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setForm({
        name: '',
        email: '',
        company: '',
        phone: '',
        region: '',
        interest: '',
        message: '',
      });
      setState({ loading: false, success: false, error: null });
    }, 500);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid={SITE.book.dialog}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.button
            aria-label="Close"
            onClick={close}
            className="absolute inset-0 bg-black/80 cursor-default"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 16, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-[820px] max-h-[90vh] overflow-y-auto bg-[color:var(--ink-2)] border border-[color:var(--rule-dark-2)] rounded-sm p-6 md:p-10"
          >
            <button
              data-testid={SITE.book.close}
              onClick={close}
              className="absolute top-4 right-4 w-9 h-9 rounded-full border border-[color:var(--rule-dark-2)] text-[color:var(--stone)] hover:border-[color:var(--gold-2)] hover:text-[color:var(--gold-2)] transition-colors"
              aria-label="Close"
            >
              ×
            </button>

            {state.success ? (
              <div
                data-testid={SITE.book.success}
                className="py-14 text-center max-w-[520px] mx-auto"
              >
                <p className="eyebrow">Received</p>
                <h3 className="mt-6 font-serif text-[32px] md:text-[44px] leading-[1.05] tracking-[-0.02em] text-[color:var(--stone)]">
                  Message received.
                </h3>
                <p className="mt-6 text-[15px] leading-[1.65] text-[color:var(--stone-2)]">
                  A partner will reach out directly within one business day.
                  For anything time-sensitive, please write to{' '}
                  <a
                    href="mailto:mk@no-bs.in"
                    className="text-[color:var(--gold-2)]"
                  >
                    mk@no-bs.in
                  </a>
                  .
                </p>
                <button onClick={close} className="mt-10 btn-primary">
                  Close
                </button>
              </div>
            ) : (
              <>
                <p className="eyebrow">Start a conversation</p>
                <h3 className="mt-3 font-serif text-[28px] md:text-[36px] leading-[1.05] tracking-[-0.02em] text-[color:var(--stone)]">
                  Share the outline. We&apos;ll reply personally within one
                  business day.
                </h3>
                <p className="mt-4 text-[14px] leading-[1.6] text-[color:var(--stone-2)] max-w-[560px]">
                  All submissions are read by a partner. Held in strict
                  confidence. No mailing lists, no marketing follow-ups.
                </p>

                <form
                  onSubmit={submit}
                  className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
                >
                  <Field label="Name*">
                    <input
                      data-testid={SITE.book.name}
                      className="field-dark"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={update('name')}
                      required
                    />
                  </Field>
                  <Field label="Email*">
                    <input
                      data-testid={SITE.book.email}
                      className="field-dark"
                      type="email"
                      placeholder="you@company.com"
                      value={form.email}
                      onChange={update('email')}
                      required
                    />
                  </Field>
                  <Field label="Company">
                    <input
                      data-testid={SITE.book.company}
                      className="field-dark"
                      placeholder="Company / family office"
                      value={form.company}
                      onChange={update('company')}
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      data-testid={SITE.book.phone}
                      className="field-dark"
                      placeholder="+91 ..."
                      value={form.phone}
                      onChange={update('phone')}
                    />
                  </Field>
                  <Field label="Region">
                    <select
                      data-testid={SITE.book.region}
                      className="field-dark appearance-none"
                      value={form.region}
                      onChange={update('region')}
                    >
                      <option value="" style={{ background: '#16161a' }}>
                        Select region
                      </option>
                      {REGIONS.map((r) => (
                        <option
                          key={r}
                          value={r}
                          style={{ background: '#16161a' }}
                        >
                          {r}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Interest">
                    <select
                      data-testid={SITE.book.interest}
                      className="field-dark appearance-none"
                      value={form.interest}
                      onChange={update('interest')}
                    >
                      <option value="" style={{ background: '#16161a' }}>
                        Select an area
                      </option>
                      {INTERESTS.map((r) => (
                        <option
                          key={r}
                          value={r}
                          style={{ background: '#16161a' }}
                        >
                          {r}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Brief">
                      <textarea
                        data-testid={SITE.book.message}
                        className="field-dark"
                        rows={4}
                        placeholder="A few lines about the situation."
                        value={form.message}
                        onChange={update('message')}
                      />
                    </Field>
                  </div>

                  <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
                    <p className="small-caps text-[10.5px] text-[color:var(--stone-3)]">
                      Confidential · Partner-level review
                    </p>
                    <button
                      data-testid={SITE.book.submit}
                      type="submit"
                      disabled={state.loading}
                      className="btn-primary disabled:opacity-60"
                    >
                      {state.loading ? 'Sending…' : 'Send'}
                      <span aria-hidden>→</span>
                    </button>
                  </div>

                  {state.error && (
                    <div className="md:col-span-2 text-[13px] text-red-300/90">
                      {state.error}
                    </div>
                  )}
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block small-caps text-[10.5px] text-[color:var(--stone-3)] mb-1.5">
      {label}
    </span>
    {children}
  </label>
);

export default BookConsultation;
